<template>
  <div>
    <v-app-bar color="primary" flat app dark>
      <v-app-bar-nav-icon style="position: absolute" @click="goBack">
        <v-icon size="large" color="white">mdi-chevron-left</v-icon>
      </v-app-bar-nav-icon>
      <v-spacer />
      <v-app-bar-title style="min-width: fit-content; font-size: 14px;">
        {{ $t('aiReport.title') }}
      </v-app-bar-title>
      <v-spacer />
    </v-app-bar>

    <v-main>
      <div class="ma-3 d-flex align-center" style="gap: 0.5rem;">
        <span class="text-body-2 font-weight-bold">{{ $t('aiReport.fiscalYear') }}</span>
        <v-btn variant="outlined" size="small" @click="changeYear(-1)">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <span class="text-h6 font-weight-bold">{{ $t('aiReport.top.yearLabel', { year }) }}</span>
        <v-btn variant="outlined" size="small" @click="changeYear(1)">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>

      <v-progress-linear v-if="isFetching" indeterminate color="primary" />

      <div v-if="top" class="ma-3 d-flex flex-column" style="gap: 0.75rem;">
        <v-card
          v-for="q in top.quarters"
          :key="q.quarter"
          variant="outlined"
          class="pa-4"
          :class="cardClass(q)"
          @click="toQuarter(q.quarter)"
        >
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-overline text-grey">{{ $t('aiReport.quarterLabel', { quarter: q.quarter }) }}</div>
              <div class="text-h6 font-weight-bold">{{ monthsLabelWithNames(q.monthsLabel) }}</div>
            </div>
            <v-icon color="primary">mdi-chevron-right</v-icon>
          </div>

          <v-divider class="my-2" />

          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center" style="gap: 0.3rem;">
              <ai-report-status-chip status="published" />
              <span class="text-body-2 text-primary font-weight-bold">{{ q.publishedCount }}</span>
              <ai-report-status-chip v-if="q.draftCount > 0" status="draft" />
              <span v-if="q.draftCount > 0" class="text-body-2 text-amber-darken-3 font-weight-bold">{{ q.draftCount }}</span>
              <ai-report-status-chip v-if="q.notGeneratedCount > 0" status="notGenerated" />
              <span v-if="q.notGeneratedCount > 0" class="text-body-2 text-grey font-weight-bold">{{ q.notGeneratedCount }}</span>
            </div>
            <span class="text-caption text-grey">{{ $t('aiReport.top.totalChildren', { count: q.totalChildren }) }}</span>
          </div>

          <v-progress-linear
            :model-value="publishProgress(q)"
            color="primary"
            bg-color="grey-lighten-3"
            height="6"
            rounded
            class="mt-3"
          />
        </v-card>
      </div>

      <v-alert
        v-if="!isFetching && top && top.quarters.every(isQuarterEmpty)"
        type="info"
        variant="outlined"
        class="ma-3"
      >
        {{ $t('aiReport.top.empty') }}
      </v-alert>
    </v-main>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import AiReportStatusChip from "@/components/molecules/AiReportStatusChip.vue";
import { aiReportModule } from "@/store/dataModules/aiReportModule";
import { getFiscalYear } from "@/service/CommonUtils";
import { formatMonthsLabelWithNames } from "@/utils/aiReport";
import type { AiReportQuarter, AiReportQuarterSummary } from "@/api/ApiAiReport";

interface DataType {
  year: number;
  isFetching: boolean;
}

export default defineComponent({
  components: { AiReportStatusChip },
  data: (): DataType => ({
    year: Number(getFiscalYear()),
    isFetching: false,
  }),
  computed: {
    top() {
      return aiReportModule.top;
    },
  },
  watch: {
    year() {
      this.loadTop();
    },
  },
  mounted() {
    this.loadTop();
  },
  methods: {
    async loadTop() {
      this.isFetching = true;
      try {
        await aiReportModule.fetchTop({ businessYear: this.year });
      } finally {
        this.isFetching = false;
      }
    },
    changeYear(delta: number) {
      this.year += delta;
    },
    toQuarter(quarter: AiReportQuarter) {
      this.$router.push(`/ai-report/quarter/${this.year}/${quarter}`);
    },
    goBack() {
      sessionStorage.setItem("targetTab", "record");
      this.$router.push("/");
    },
    publishProgress(q: AiReportQuarterSummary): number {
      if (q.totalChildren === 0) return 0;
      return Math.floor((q.publishedCount / q.totalChildren) * 100);
    },
    isQuarterEmpty(q: AiReportQuarterSummary): boolean {
      return q.publishedCount === 0 && q.draftCount === 0;
    },
    monthsLabelWithNames(monthsLabel: string): string {
      return formatMonthsLabelWithNames(monthsLabel);
    },
    cardClass(q: AiReportQuarterSummary): string {
      if (q.publishedCount === q.totalChildren && q.totalChildren > 0) {
        return "ai-report-quarter-card ai-report-quarter-card--done";
      }
      return "ai-report-quarter-card";
    },
  },
});
</script>

<style lang="scss" scoped>
.ai-report-quarter-card {
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  &--done {
    border-color: rgb(var(--v-theme-primary)) !important;
    background-color: #f5fbfc;
  }
}
</style>
